"use client";

import { useEffect, useState } from "react";
import { AffiliateImpression } from "@/components/affiliate/affiliate-impression";
import { AffiliateLink } from "@/components/affiliate/affiliate-link";
import { offerGoSlug } from "@/lib/affiliate/go-link";
import type { AffiliatePlatform } from "@/lib/affiliate/links";
import { updatePendingApply } from "@/lib/apply/pending-apply-store";
import type { Locale } from "@/lib/i18n/config";
import type { PostApplyOffer as Offer } from "@/lib/monetization/post-apply-offers";
import { useStickyFooterClaimed } from "@/lib/monetization/sticky-footer-claim";
import { SponsoredLabel } from "./sponsored-label";

const PENDING_KEY = "ld_post_apply_pending";

/** Fired synchronously on the outbound apply click so the in-page after-apply
 * unit (`AfterApplyInline`) can reveal immediately, without waiting for the
 * tab-return `visibilitychange` the popup depends on. */
export const APPLY_CLICKED_EVENT = "ld:apply-clicked";

/** Marked by the apply CTA on outbound click; read here when the user returns. */
export function markPostApplyPending(
	jobId: string,
	job?: { company?: string | null; title?: string | null },
): void {
	// Name the employer in the next-visit "did you apply?" ask. Enrich-only:
	// the entry itself is written by the toast's outbound-link listener, which
	// sees only real `/out/` clicks (an apply-by-email compose link is not one).
	if (job) {
		updatePendingApply(jobId, {
			company: job.company ?? null,
			title: job.title ?? null,
		});
	}
	try {
		sessionStorage.setItem(PENDING_KEY, jobId);
	} catch {
		/* sessionStorage unavailable — no post-apply card, never break apply */
	}
	try {
		window.dispatchEvent(
			new CustomEvent(APPLY_CLICKED_EVENT, { detail: { jobId } }),
		);
	} catch {
		/* no window / CustomEvent — inline unit simply never reveals, apply is untouched */
	}
}

/** Clears the pending flag so the tab-return popup self-suppresses. Called by
 * the inline after-apply unit when it takes the offer, so an apply never shows
 * BOTH the inline unit and the popup (one offer per apply). */
export function clearPostApplyPending(): void {
	try {
		sessionStorage.removeItem(PENDING_KEY);
	} catch {
		/* ignore */
	}
}

/**
 * Shows a single, dismissible post-apply offer card when the user returns to the
 * tab after clicking Apply for THIS job. The offer is chosen server-side by the
 * engine (`/api/monetization/post-apply-offer`, force-dynamic so market is
 * reliable). Renders nothing unless the engine returns one — so it is inert
 * until the `monetization-engine` flag is on with the `post_apply` surface.
 */
export function PostApplyOffer({
	jobId,
	categoryId,
	locale,
}: {
	jobId: string;
	categoryId: string | null;
	locale: Locale;
}) {
	const [offer, setOffer] = useState<Offer | null>(null);
	const [dismissed, setDismissed] = useState(false);
	// B3 (growth-funnel-retention-audit-2026-08-06): this card was the one
	// `fixed bottom-*` surface never wired into the sticky-footer claim
	// protocol (see `sticky-footer-claim.tsx`'s doc comment for the
	// recurring "ambient nag covers the primary Apply CTA" class this
	// closes) — a z-50 popup could paint directly over the mobile
	// `job-apply-cta.tsx` fixed footer (z-40, same bottom-0 slot) the moment
	// the user tabs back in, with no dismiss-free path to the CTA underneath.
	const stickyFooterClaimed = useStickyFooterClaimed();

	useEffect(() => {
		let shown = false;

		const onVisible = () => {
			if (shown || document.visibilityState !== "visible") return;
			let pending: string | null = null;
			try {
				pending = sessionStorage.getItem(PENDING_KEY);
			} catch {
				return;
			}
			if (pending !== jobId) return;
			shown = true;
			try {
				sessionStorage.removeItem(PENDING_KEY);
			} catch {
				/* ignore */
			}

			const params = new URLSearchParams({ locale });
			if (categoryId) params.set("category", categoryId);
			fetch(`/api/monetization/post-apply-offer?${params.toString()}`)
				.then((r) => (r.ok ? r.json() : null))
				.then((data) => {
					const parsed = data as { offer?: Offer | null } | null;
					if (parsed?.offer) setOffer(parsed.offer);
				})
				.catch(() => undefined);
		};

		document.addEventListener("visibilitychange", onVisible);
		return () => document.removeEventListener("visibilitychange", onVisible);
	}, [jobId, categoryId, locale]);

	if (!offer || dismissed || stickyFooterClaimed) return null;

	return (
		<div className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-sm sm:left-auto sm:right-4 sm:mx-0">
			<AffiliateImpression
				platform={offer.platform}
				surface="post_apply"
				locale={locale}
				jobId={jobId}
				className="rounded-2xl border border-border bg-card p-4 shadow-lg"
			>
				<div className="mb-1 flex items-center justify-between gap-2">
					<SponsoredLabel
						locale={locale}
						advertiseHref={`/${locale === "en" ? "en" : "id"}/advertise?src=post_apply`}
					/>
					<button
						type="button"
						onClick={() => setDismissed(true)}
						aria-label={locale === "en" ? "Dismiss" : "Tutup"}
						className="text-muted-foreground hover:text-foreground text-sm leading-none"
					>
						×
					</button>
				</div>
				<p className="text-sm font-semibold text-foreground">{offer.title}</p>
				<p className="mt-0.5 text-xs text-muted-foreground">{offer.desc}</p>
				<AffiliateLink
					platform={offer.platform as AffiliatePlatform | "educative"}
					href={offer.href}
					goSlug={offerGoSlug({ provider: offer.platform, href: offer.href })}
					category={categoryId}
					surface="post_apply"
					jobId={jobId}
					locale={locale}
					className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
				>
					{offer.cta}
				</AffiliateLink>
			</AffiliateImpression>
		</div>
	);
}
